import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import { MessageSquare, Target, AlertTriangle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'react-hot-toast';

// Types
interface TeamDynamicsData {
  metrics: Record<string, number>;
  insights: Record<string, string>;
}

interface LeadershipFollowershipData {
  leadership: {
    effectiveness: number;
    description: string;
  };
  followership: {
    effectiveness: number;
    description: string;
  };
  recommendations: string[];
}

interface CollaborationCapabilitiesData {
  capabilities: Array<{
    title: string;
    score: number; 
    description: string;
  }>;
}

interface ConflictResolutionData {
  skills: Array<{
    title: string;
    selfScore: number;
    evaluatorScore: number;
    description: string;
  }>;
}

interface SBIFeedbackData {
  examples: Array<{
    title: string;
    situation: string;
    behavior: string;
    impact: string;
  }>;
  takeaways: {
    strengths: string[];
    areasForGrowth: string[];
    actionItems: string[];
  };
}

// -----------------------------------------------------------------------------
// Chart Configurations
// -----------------------------------------------------------------------------
const teamChartOptions = {
  responsive: true,
  indexAxis: 'y' as const,
  scales: {
    x: {
      beginAtZero: true,
      max: 5,
      grid: { display: false }
    },
    y: { grid: { display: false } }
  },
  plugins: {
    legend: { display: false }
  }
};

const pieOptions = {
  responsive: true,
  cutout: '60%',
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        padding: 20,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    }
  }
};

// Chart data for leadership vs followership
const leadershipChartData = {
  labels: ['Leadership Effectiveness', 'Followership Effectiveness'],
  datasets: [
    {
      data: [60, 40],
      backgroundColor: [
        'rgba(238, 153, 234, 0.8)',
        'rgba(88, 211, 226, 0.8)'
      ],
      borderColor: [
        'rgb(238, 153, 234)',
        'rgb(88, 211, 226)'
      ],
      borderWidth: 1
    }
  ]
};

// -----------------------------------------------------------------------------
// Subcomponents
// -----------------------------------------------------------------------------

// Loading Component
function LoadingState() {
  return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
    </div>
  );
}

// Error State Component
function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-8 text-center">
      <div className="flex justify-center mb-4">
        <AlertTriangle className="w-12 h-12 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Data Not Available
      </h3>
      <p className="text-gray-600 max-w-lg mx-auto">
        {message}
      </p>
    </div>
  );
}

// 1. Team Dynamics Overview Section
function TeamDynamicsOverview() {
  const [showMore, setShowMore] = useState(false);
  const { currentUser } = useAuth();
  const [teamDynamicsData, setTeamDynamicsData] = useState<TeamDynamicsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTeamDynamicsData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.collaborator_genius_overview) {
            const overviewData = JSON.parse(data.collaborator_genius_overview);
            setTeamDynamicsData(overviewData);
          }
        }
      } catch (error) {
        console.error('Error loading team dynamics data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadTeamDynamicsData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!teamDynamicsData) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="w-12 h-12 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Team Dynamics Data Not Available
        </h3>
        <p className="text-gray-600 max-w-lg mx-auto">
          The team dynamics analysis will be available once your report is generated.
        </p>
      </div>
    );
  }

  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Team Dynamics Overview</h2>
      </div>

      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Evaluate your foundational role in team collaboration and alignment. This section highlights how you influence team trust, role clarity, collaboration frequency, and decision-making effectiveness.
        </p>
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>The Team Dynamics Overview section provides a clear and actionable assessment of your impact on team functioning by analyzing four key metrics:</p>
            {['Trust', 'Role Clarity', 'Collaboration Frequency', 'Influence'].map((metric) => (
              <div key={metric}>
                <h4 className="font-medium text-gray-900 mb-2">{metric}</h4>
                <p>
                  {metric === 'Trust' &&
                    "Reflects how reliable and dependable others perceive you to be in team settings. High scores indicate strong team confidence in your integrity and reliability."}
                  {metric === 'Role Clarity' &&
                    "Measures how well individual roles and responsibilities are defined and understood within your team. Clearer roles lead to better coordination and reduced conflict."}
                  {metric === 'Collaboration Frequency' &&
                    "Captures how often you engage in cooperative efforts with team members. Higher scores signify active participation and teamwork."}
                  {metric === 'Influence' &&
                    "Evaluates your ability to guide and align team decisions and actions effectively. Strong influence suggests a capacity to inspire and steer group outcomes."}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.svg>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="col-span-1 lg:col-span-2">
          {/* Team Dynamics Metrics Chart */}
          <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6 mb-8">
            <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Dynamics Metrics</h3>
              <div className="h-[300px]">{
                teamDynamicsData && (
                  <Bar 
                    data={{
                      labels: Object.keys(teamDynamicsData.metrics),
                      datasets: [{
                        label: 'Team Dynamics Assessment',
                        data: Object.values(teamDynamicsData.metrics),
                        backgroundColor: [
                          'rgba(255, 99, 132, 0.7)',
                          'rgba(255, 206, 86, 0.7)',
                          'rgba(75, 192, 192, 0.7)',
                          'rgba(153, 102, 255, 0.7)'
                        ],
                        borderColor: [
                          'rgb(255, 99, 132)',
                          'rgb(255, 206, 86)',
                          'rgb(75, 192, 192)',
                          'rgb(153, 102, 255)'
                        ],
                        borderWidth: 1,
                        borderRadius: 4
                      }]
                    }} 
                    options={teamChartOptions} 
                  />
                )
              }</div>
            </div>
          </div>

          {/* Insights Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(teamDynamicsData.insights).map(([metric, insight]) => (
              <div key={metric} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h4 className="font-medium text-gray-900 mb-2">{metric}</h4>
                <p className="text-gray-600">{insight}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Score</span>
                  <span className="text-sm font-medium text-primary">
                    {teamDynamicsData.metrics[metric]}/5
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// 2. Collaboration Capabilities Section
function CollaborationCapabilities() {
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [capabilitiesData, setCapabilitiesData] = useState<CollaborationCapabilitiesData | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    async function loadCapabilitiesData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.collaborator_genius_collab_cap) {
            const capabilitiesData = JSON.parse(data.collaborator_genius_collab_cap);
            setCapabilitiesData(capabilitiesData);
          }
        }
      } catch (error) {
        console.error('Error loading capabilities data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadCapabilitiesData();
  }, [currentUser]);

  if (loading) {
    return <LoadingState />;
  }

  if (!capabilitiesData) {
    return <ErrorState message="The collaboration capabilities analysis will be available once your report is generated." />;
  }

  // Ensure we have valid capabilities data
  if (!Array.isArray(capabilitiesData.capabilities) || capabilitiesData.capabilities.length === 0) {
    return <ErrorState message="Invalid collaboration capabilities data. Please contact support." />;
  }

  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Collaboration Capabilities</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Discover how effectively you contribute to shared goals by evaluating four key dimensions of collaboration: task coordination, participation, shared goal alignment, and supportiveness. This section provides clear insights into your collaboration patterns over time, helping you identify strengths and opportunities for improvement.
        </p>
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            {[
              {
                title: "Task Coordination",
                description: "Measures your ability to organize and synchronize activities, ensuring tasks are completed efficiently and deadlines are met."
              },
              {
                title: "Participation",
                description: "Evaluates the extent and quality of your engagement in collaborative efforts, reflecting your involvement in discussions, decisions, and actions."
              },
              {
                title: "Shared Goal Alignment",
                description: "Examines how well your efforts align with the team's overarching objectives, highlighting your ability to prioritize collective success over individual agendas."
              },
              {
                title: "Supportiveness",
                description: "Captures how effectively you assist and uplift team members, fostering a positive and collaborative team atmosphere."
              }
            ].map((item) => (
              <div key={item.title}>
                <h4 className="font-medium text-gray-900 mb-2">{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.svg>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {capabilitiesData?.capabilities.map((item, index) => {
          const colorSets = [
            {
              colors: ['rgba(238, 153, 234, 0.8)', 'rgba(238, 153, 234, 0.1)'],
              borderColors: ['rgb(238, 153, 234)', 'transparent']
            },
            {
              colors: ['rgba(88, 211, 226, 0.8)', 'rgba(88, 211, 226, 0.1)'],
              borderColors: ['rgb(88, 211, 226)', 'transparent']
            },
            {
              colors: ['rgba(249, 197, 95, 0.8)', 'rgba(249, 197, 95, 0.1)'],
              borderColors: ['rgb(249, 197, 95)', 'transparent']
            },
            {
              colors: ['rgba(34, 197, 94, 0.8)', 'rgba(34, 197, 94, 0.1)'],
              borderColors: ['rgb(34, 197, 94)', 'transparent']
            }
          ];
          
          const colorSet = colorSets[index % colorSets.length];
          
          return (
          <div key={item.title} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">{item.title}</h3>
            <div className="h-[200px] relative">
              <Doughnut
                data={{
                  labels: ['Score', 'Remaining'],
                  datasets: [
                    {
                      data: [item.score, 100 - item.score],
                      backgroundColor: colorSet.colors,
                      borderColor: colorSet.borderColors,
                      borderWidth: 1
                    }
                  ]
                }}
                options={{
                  cutout: '70%',
                  plugins: { legend: { display: false } }
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{item.score}%</div>
                  <div className="text-sm text-gray-500">Score</div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">{item.description}</p>
          </div>
        )})}
      </div>
    </section>
  );
}

// 3. Leadership vs. Followership Section
function LeadershipFollowershipSection() {
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leadershipData, setLeadershipData] = useState<LeadershipFollowershipData | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    async function loadLeadershipData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.collaborator_genius_lead_follow) {
            const leadershipData = JSON.parse(data.collaborator_genius_lead_follow);
            setLeadershipData(leadershipData);
          }
        }
      } catch (error) {
        console.error('Error loading leadership data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadLeadershipData();
  }, [currentUser]);

  if (loading) {
    return <LoadingState />;
  }

  if (!leadershipData) {
    return <ErrorState message="The leadership vs. followership analysis will be available once your report is generated." />;
  }

  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Leadership vs. Followership</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Understand your ability to balance leading and supporting roles for optimal team outcomes. This section highlights how you excel in leadership and followership, providing guidance on calibrating your approach based on your role and expectations.
        </p>
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>The Leadership vs. Followership section evaluates your effectiveness in adopting the appropriate role—leading or supporting—based on the team's needs and context:</p>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Leadership Effectiveness</h4>
              <p>Reflects your ability to guide, inspire, and take initiative when leading is required. A high score indicates confidence, decisiveness, and the capacity to motivate and align your team.</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Followership Effectiveness</h4>
              <p>Highlights how effectively you contribute as a supportive team member by following directions, collaborating, and enabling collective success. High scores suggest adaptability, humility, and a willingness to empower others.</p>
            </div>
            <p>This section recognizes that a balanced approach is not always necessary. Instead, it helps you understand when to lead and when to support, ensuring alignment with your role, delivery expectations, and team dynamics.</p>
          </div>
        </motion.div>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.svg>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Chart Section */}
        <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
          <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Distribution</h3>
            <div className="h-[400px]">
              <Pie data={leadershipChartData} options={pieOptions} />
            </div>
            <div className="mt-4 text-center text-sm text-gray-500">
              Click segments to see detailed information
            </div>
          </div>
        </div>
        {/* Insights Section */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <h4 className="font-medium text-gray-900 mb-4">Leadership Effectiveness</h4>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">Score</span>
              <span className="text-sm font-medium text-primary">
                {leadershipData.leadership.effectiveness}%
              </span>
            </div>
            <p className="text-gray-600">{leadershipData.leadership.description}</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <h4 className="font-medium text-gray-900 mb-4">Followership Effectiveness</h4>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">Score</span>
              <span className="text-sm font-medium text-secondary">
                {leadershipData.followership.effectiveness}%
              </span>
            </div>
            <p className="text-gray-600">{leadershipData.followership.description}</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <h4 className="font-medium text-gray-900 mb-4">Recommendations</h4>
            <ul className="space-y-3">
              {leadershipData.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-gray-600">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// 4. Conflict and Resolution Skills Section
function ConflictResolutionSkillsSection() {
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conflictData, setConflictData] = useState<ConflictResolutionData | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    async function loadConflictData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);
        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.collaborator_genius_conflict_res) {
            const conflictData = JSON.parse(data.collaborator_genius_conflict_res);
            setConflictData(conflictData);
          }
        }
      } catch (error) {
        console.error('Error loading conflict resolution data:', error);
        toast.error('Failed to load conflict resolution data');
      } finally {
        setLoading(false);
      }
    }

    loadConflictData();
  }, [currentUser]);

  if (loading) {
    return <LoadingState />;
  }

  if (!conflictData) {
    return <ErrorState message="The conflict resolution analysis will be available once your report is generated." />;
  }

  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Conflict and Resolution Skills</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          Gain insights into your ability to handle disagreements constructively by comparing your self-assessment with evaluators' perceptions. This analysis focuses on four critical skills: compromise, assertiveness, active listening, and problem-solving, helping you refine your approach to conflict resolution.
        </p>
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>Each Double Line Doughnut Chart represents one dimension, with the inner ring showing your self-assessment score and the outer ring displaying the evaluators' average:</p>
            {[
              {
                title: "Compromise",
                description: "Measures your ability to find mutually beneficial solutions during conflicts. This skill reflects how adaptable and collaborative you are in resolving disagreements."
              },
              {
                title: "Assertiveness",
                description: "Examines your capability to express ideas confidently while maintaining respect for others' opinions. Balancing assertiveness with consideration is crucial for constructive conflict management."
              },
              {
                title: "Active Listening",
                description: "Captures how well you listen, validate others' perspectives, and use feedback effectively. Strong active listening fosters trust and ensures open communication during disputes."
              },
              {
                title: "Problem-Solving",
                description: "Assesses your ability to analyze conflict, identify solutions, and implement actions to resolve disagreements. This dimension reflects logical thinking and action-oriented decision-making."
              }
            ].map((item) => (
              <div key={item.title}>
                <h4 className="font-medium text-gray-900 mb-2">{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.svg>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {conflictData.skills.map((skill, index) => (
          <div key={skill.title} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">{skill.title}</h3>
            <div className="h-[200px] relative">
              <Doughnut
                data={{
                  labels: ['Self Assessment', 'Evaluators Average'],
                  datasets: [
                    {
                      data: [skill.selfScore, 100 - skill.selfScore],
                      backgroundColor: ['rgba(238, 153, 234, 0.8)', 'rgba(238, 153, 234, 0.1)'],
                      borderColor: ['rgb(238, 153, 234)', 'transparent'],
                      borderWidth: 1,
                      weight: 1
                    },
                    {
                      data: [skill.evaluatorScore, 100 - skill.evaluatorScore],
                      backgroundColor: ['rgba(88, 211, 226, 0.8)', 'rgba(88, 211, 226, 0.1)'],
                      borderColor: ['rgb(88, 211, 226)', 'transparent'],
                      borderWidth: 1,
                      weight: 0.8
                    }
                  ]
                }}
                options={{ cutout: '70%', plugins: { legend: { display: false } } }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{skill.selfScore}%</div>
                  <div className="text-sm text-gray-500">Self</div>
                  <div className="text-2xl font-bold text-secondary mt-1">{skill.evaluatorScore}%</div>
                  <div className="text-sm text-gray-500">Evaluators</div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">{skill.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// 5. SBI Feedback Analysis Section
function SBIFeedbackAnalysisSection() {
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sbiData, setSBIData] = useState<SBIFeedbackData | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    async function loadFeedbackData() {
      if (!currentUser) return;

      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);

        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.collaborator_genius_sbi) {
            const parsedData = JSON.parse(data.collaborator_genius_sbi);
            setSBIData(parsedData);
          }
        }
      } catch (error) {
        console.error('Error loading SBI feedback data:', error);
        toast.error('Failed to load feedback analysis data');
      } finally {
        setLoading(false);
      }
    }

    loadFeedbackData();
  }, [currentUser]);

  if (loading) {
    return <LoadingState />;
  }

  if (!sbiData) {
    return <ErrorState message="The SBI feedback analysis will be available once your report is generated." />;
  }

  return (
    <section className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">SBI Feedback Analysis</h2>
      </div>
      <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
        <p className="text-gray-600 leading-relaxed mb-4">
          The SBI Feedback model organizes feedback into three actionable parts: the Situation, the Behavior, and the Impact. This section helps you review and reflect on feedback constructively to foster growth, improve interactions, and enhance collaboration.
        </p>
        <motion.div
          initial={false}
          animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
            <p>The SBI Feedback model is a structured framework designed to make feedback more actionable and meaningful by breaking it into three parts:</p>
            {[
              {
                title: "Situation",
                description: "Identifies the time, place, and context of the observed behavior."
              },
              {
                title: "Behavior",
                description: "Describes the specific actions without judgment or interpretation."
              },
              {
                title: "Impact",
                description: "Explains the effects of the behavior on individuals, teams, or the organization."
              }
            ].map((item) => (
              <div key={item.title}>
                <h4 className="font-medium text-gray-900 mb-2">{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
            <p>This section empowers you to reflect on detailed feedback, provided by evaluators and self-assessments, helping you identify patterns and areas of improvement.</p>
          </div>
        </motion.div>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
        >
          <span>{showMore ? 'Read less' : 'Read more'}</span>
          <motion.svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            animate={{ rotate: showMore ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.svg>
        </button>
      </div>

      <div className="space-y-6">
        {/* SBI Feedback Examples */}
        {sbiData.examples.map((example, index) => (
          <motion.div
            key={example.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100"
          >
            <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-4">
              <h3 className="text-lg font-semibold text-gray-900">{example.title}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span>Situation</span>
                </h4>
                <p className="text-gray-600 ml-4">{example.situation}</p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <span className="w-2 h-2 bg-secondary rounded-full"></span>
                  <span>Behavior</span>
                </h4>
                <p className="text-gray-600 ml-4">{example.behavior}</p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#f9c55f] rounded-full"></span>
                  <span>Impact</span>
                </h4>
                <p className="text-gray-600 ml-4">{example.impact}</p>
              </div>
            </div>
          </motion.div>
        ))}

        {/* Key Takeaways */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6"
        >
          <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Key Takeaways</h3>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-medium text-gray-900 mb-2">Strengths to Leverage</h4>
                <ul className="space-y-2">
                  {sbiData.takeaways.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span className="text-gray-600">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-medium text-gray-900 mb-2">Areas for Growth</h4>
                <ul className="space-y-2">
                  {sbiData.takeaways.areasForGrowth.map((area, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span className="text-gray-600">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-medium text-gray-900 mb-2">Action Items</h4>
                <ul className="space-y-2">
                  {sbiData.takeaways.actionItems.map((action, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span className="text-gray-600">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------
function CollaboratorGeniusView() {
  return (
    <div className="p-6">
      <TeamDynamicsOverview />
      <CollaborationCapabilities />
      <LeadershipFollowershipSection />
      <ConflictResolutionSkillsSection />
      <SBIFeedbackAnalysisSection />
    </div>
  );
}

// Export the component only once
export { CollaboratorGeniusView };